import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ExternalLink, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Button, Card, CardTitle, PageHeader } from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import type { ResponseEnvelope } from '@/types/api';
import { Endpoints } from '@/lib/api/endpoints';

interface PasswordUpdateResult {
  message: string;
  redirectHint?: string;
}

export function UserSecurityPage() {
  const [result, setResult] = useState<PasswordUpdateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestMutation = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post<ResponseEnvelope<PasswordUpdateResult>>(
          Endpoints.meProfile.requestPasswordUpdate,
        ),
      ),
    onSuccess: (data: PasswordUpdateResult) => {
      setResult(data);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(isApiError(err) ? err.messages[0]?.message ?? 'Request failed' : 'Request failed — please try again');
    },
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title="Security"
        subtitle="Manage your account security settings"
        badge={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400">
            <ShieldCheck className="h-3 w-3" />
            Secured
          </span>
        }
      />

      {/* Password */}
      <Card>
        <CardTitle className="mb-1">
          <span className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-fg-muted" />
            Password
          </span>
        </CardTitle>
        <p className="mb-4 text-sm text-fg-muted">
          Your password is managed securely by Authmatech Identity. To change it, we'll send a
          password reset link to your registered email address.
        </p>

        {result ? (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              <div>
                <p className="text-sm font-semibold text-emerald-300">Email sent</p>
                <p className="mt-0.5 text-sm text-emerald-400">{result.message}</p>
                {result.redirectHint && (
                  <a
                    href={result.redirectHint}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-400 underline-offset-2 hover:underline"
                  >
                    Open Account Portal
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <p className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-400">
                {error}
              </p>
            )}
            <Button
              onClick={() => requestMutation.mutate()}
              disabled={requestMutation.isPending}
              className="flex items-center gap-2"
            >
              <Lock className="h-4 w-4" />
              {requestMutation.isPending ? 'Sending…' : 'Start Secure Password Update'}
            </Button>
          </>
        )}
      </Card>

      {/* Active Sessions placeholder */}
      <Card>
        <CardTitle className="mb-1">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-fg-muted" />
            Active Sessions
          </span>
        </CardTitle>
        <p className="text-sm text-fg-muted">
          Session management is handled by Authmatech Identity. To view or terminate active
          sessions, visit your account portal.
        </p>
      </Card>
    </div>
  );
}
